import React, { useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";

const ChatMessage = ({ messages = [], isTyping = false }) => {
  const outputRef = useRef(null);

  useEffect(() => {
    if (outputRef?.current) {
      outputRef.current.scrollTop = outputRef?.current?.scrollHeight;
    }
  }, [messages, isTyping]);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getMessageColor = (type) => {
    switch (type) {
      case "success":
        return "text-success";
      case "error":
        return "text-destructive";
      case "warning":
        return "text-warning";
      case "info":
        return "text-terminal-blue";
      case "system":
        return "text-terminal-cyan";
      case "command":
        return "text-primary";
      default:
        return "text-foreground";
    }
  };

  return (
    <div
      ref={outputRef}
      className="flex-1 overflow-y-auto space-y-3 pr-2 scrollbar-thin scrollbar-track-muted/20 scrollbar-thumb-primary/30"
      style={{ maxHeight: "400px" }}
    >
      {messages?.map((message, index) => {
        const isUserMessage =
          message?.type === "command" || message?.sender === "user";

        return (
          <div
            key={index}
            className={`flex items-start space-x-2 ${
              isUserMessage ? "flex-row-reverse" : "justify-start"
            }`}
          >
            {/* Avatar */}
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                isUserMessage
                  ? "bg-primary text-primary-foreground ml-2"
                  : "bg-muted text-muted-foreground"
              }`}
              title={isUserMessage ? "You" : "DeveloperNoob"}
            >
              {isUserMessage ? "Y" : "D"}
            </div>

            {/* Message bubble */}
            <div className={`max-w-[70%] ${isUserMessage ? "mr-2" : "ml-2"}`}>
              <div
                className={`
                  rounded-2xl px-4 py-2 relative
                  ${
                    isUserMessage
                      ? "bg-primary text-primary-foreground rounded-br-md text-right"
                      : "bg-card border border-border rounded-bl-md"
                  }
                `}
              >
                {/* Message content */}
                {message?.command && isUserMessage && (
                  <div className="font-fira text-sm text-right">
                    {message?.command}
                  </div>
                )}

                <div
                  className={`text-sm leading-relaxed ${
                    isUserMessage
                      ? "text-primary-foreground text-right"
                      : getMessageColor(message?.type)
                  }`}
                >
                  {message?.content?.split("\n")?.map((line, lineIndex) => (
                    <div
                      key={lineIndex}
                      className={lineIndex > 0 ? "mt-1" : ""}
                    >
                      {line}
                    </div>
                  ))}
                </div>

                {message?.data && (
                  <div className="mt-2 p-2 bg-muted/20 border border-terminal-muted rounded text-xs font-source">
                    <pre className="text-muted-foreground whitespace-pre-wrap">
                      {JSON.stringify(message?.data, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Timestamp at bottom right of message */}
                <div
                  className={`text-xs mt-2 text-right ${
                    isUserMessage
                      ? "text-primary-foreground/70"
                      : "text-muted-foreground"
                  }`}
                >
                  {formatTimestamp(message?.timestamp)}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {isTyping && (
        <div className="flex items-start space-x-2 justify-start">
          {/* System avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-bold">
            D
          </div>

          <div className="max-w-[70%] ml-2">
            <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center space-x-1">
                <span className="text-sm text-muted-foreground"> System Is Thinking</span>
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-primary rounded-full animate-bounce"></div>
                  <div
                    className="w-1 h-1 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-1 h-1 bg-primary rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
              {/* Timestamp for typing indicator */}
              <div className="text-xs text-muted-foreground mt-2 text-right">
                {formatTimestamp(Date.now())}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
